import { IResourceItem } from "@refinedev/core";
import { AlertTriangle, LayoutDashboard, ClipboardList, Settings, Users, Building2, Calendar, User, Printer, Home, BookOpen, ClipboardCheck, FolderOpen, Stethoscope, Globe, Bell, DollarSign, Percent, FileCheck, Package, FlaskConical, Microscope, UserCheck, Activity, Heart, ScanLine, FileCode, FileBarChart, Box, Truck, ArrowUpDown, Cpu, MonitorDot, Cable, Receipt, CreditCard, Clock, SlidersHorizontal, Mail, LifeBuoy, MessageSquare, MapPin, GraduationCap, Footprints, Palette, LayoutGrid, Hash, CalendarDays, TestTube2 } from "lucide-react";

// LabBOXX LIMS Resources — Tenant-only (Enterprise features handled by SaaSBOXX)
export const tenantResources: IResourceItem[] = [
  // 1. Dashboard
  {
    name: "dashboard",
    list: "/dashboard",
    meta: {
      label: "Dashboard",
      icon: <LayoutDashboard className="h-4 w-4" />,
    },
  },
  // Document Studio moved into Core Settings (2026-09-10, owner ask). The
  // ROUTE is unchanged at /document-studio so existing bookmarks and the help
  // route map keep resolving — only where it sits in the menu has changed.

  // 2. Patient Management
  {
    name: "patients",
    meta: {
      label: "Patients",
      icon: <Heart className="h-4 w-4" />,
    },
  },
  {
    name: "patient-list",
    list: "/patients",
    create: "/patients/create",
    edit: "/patients/edit/:id",
    show: "/patients/show/:id",
    meta: {
      label: "Patient Directory",
      icon: <Users className="h-4 w-4" />,
      parent: "patients",
      canDelete: true,
    },
  },
  {
    name: "referring-doctors",
    list: "/patients/doctors",
    create: "/patients/doctors/create",
    edit: "/patients/doctors/edit/:id",
    show: "/patients/doctors/show/:id",
    meta: {
      label: "Referring Doctors",
      icon: <UserCheck className="h-4 w-4" />,
      parent: "patients",
      canDelete: true,
    },
  },

  // 3. Order Management
  {
    name: "orders-mgmt",
    meta: {
      label: "Order Management",
      icon: <ClipboardList className="h-4 w-4" />,
    },
  },
  {
    name: "orders",
    list: "/orders",
    create: "/orders/create",
    edit: "/orders/edit/:id",
    show: "/orders/show/:id",
    meta: {
      label: "Orders",
      icon: <ClipboardList className="h-4 w-4" />,
      parent: "orders-mgmt",
      canDelete: true,
    },
  },
  {
    name: "accessioning",
    list: "/orders/accessioning",
    create: "/orders/accessioning/create",
    edit: "/orders/accessioning/edit/:id",
    meta: {
      label: "Accessioning / Collection",
      icon: <ScanLine className="h-4 w-4" />,
      parent: "orders-mgmt",
    },
  },
  {
    name: "generate-worklist",
    list: "/orders/worklist",
    meta: {
      label: "Generate Worklist",
      icon: <ClipboardCheck className="h-4 w-4" />,
      parent: "orders-mgmt",
    },
  },
  {
    name: "reprint-labels",
    list: "/orders/reprint",
    meta: {
      label: "Reprint Labels",
      icon: <Printer className="h-4 w-4" />,
      parent: "orders-mgmt",
    },
  },

  // 4. Laboratory
  {
    name: "laboratory",
    meta: {
      label: "Laboratory",
      icon: <FlaskConical className="h-4 w-4" />,
    },
  },
  {
    name: "appointments",
    list: "/laboratory/appointments",
    create: "/laboratory/appointments/create",
    edit: "/laboratory/appointments/edit/:id",
    show: "/laboratory/appointments/show/:id",
    meta: {
      label: "Appointments",
      icon: <Calendar className="h-4 w-4" />,
      parent: "laboratory",
      canDelete: true,
    },
  },
  {
    name: "home-visits",
    list: "/laboratory/home-visits",
    create: "/laboratory/home-visits/create",
    edit: "/laboratory/home-visits/edit/:id",
    show: "/laboratory/home-visits/show/:id",
    meta: {
      label: "Home Visits",
      icon: <Home className="h-4 w-4" />,
      parent: "laboratory",
      canDelete: true,
    },
  },
  {
    name: "test-catalog",
    list: "/laboratory/test-catalog",
    create: "/laboratory/test-catalog/create",
    edit: "/laboratory/test-catalog/edit/:id",
    show: "/laboratory/test-catalog/show/:id",
    meta: {
      label: "Test Catalog",
      icon: <BookOpen className="h-4 w-4" />,
      parent: "laboratory",
      canDelete: true,
    },
  },
  {
    name: "reference-ranges",
    list: "/laboratory/reference-ranges",
    create: "/laboratory/reference-ranges/create",
    edit: "/laboratory/reference-ranges/edit/:id",
    show: "/laboratory/reference-ranges/show/:id",
    meta: {
      label: "Reference Ranges",
      icon: <Activity className="h-4 w-4" />,
      parent: "laboratory",
      canDelete: true,
    },
  },
  {
    name: "analyzer-results",
    list: "/laboratory/analyzer-results",
    meta: {
      label: "Analyzer Results",
      icon: <FileCode className="h-4 w-4" />,
      parent: "laboratory",
    },
  },
  {
    name: "quality-control",
    list: "/laboratory/quality-control",
    meta: {
      label: "Quality Control",
      icon: <ClipboardCheck className="h-4 w-4" />,
      parent: "laboratory",
    },
  },
  {
    name: "lab-reports",
    list: "/laboratory/reports",
    create: "/laboratory/reports/create",
    edit: "/laboratory/reports/edit/:id",
    show: "/laboratory/reports/show/:id",
    meta: {
      label: "Lab Reports",
      icon: <FileBarChart className="h-4 w-4" />,
      parent: "laboratory",
      canDelete: true,
    },
  },

  // Footfall analytics (SaaSBOXX footfall plane; gated on platform_footfall)
  {
    name: "footfall",
    list: "/footfall",
    meta: {
      label: "Footfall",
      icon: <Footprints className="h-4 w-4" />,
    },
  },

  // 5. Results & Pathologist Review
  {
    name: "results",
    meta: {
      label: "Results",
      icon: <Microscope className="h-4 w-4" />,
    },
  },
  {
    name: "results-worklist",
    list: "/results/worklist",
    edit: "/results/worklist/edit/:id",
    show: "/results/worklist/show/:id",
    meta: {
      label: "Results Worklist",
      icon: <ClipboardList className="h-4 w-4" />,
      parent: "results",
      canDelete: false,
    },
  },
  {
    name: "pathologist-review",
    list: "/results/pathologist-review",
    show: "/results/pathologist-review/show/:id",
    meta: {
      label: "Pathologist Review",
      icon: <Stethoscope className="h-4 w-4" />,
      parent: "results",
    },
  },

  // 6. Inventory
  {
    name: "inventory",
    meta: {
      label: "Inventory",
      icon: <Package className="h-4 w-4" />,
    },
  },
  {
    name: "inventory-items",
    list: "/inventory/items",
    create: "/inventory/items/create",
    edit: "/inventory/items/edit/:id",
    show: "/inventory/items/show/:id",
    meta: {
      label: "Item Catalog",
      icon: <Box className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "inventory-categories",
    list: "/inventory/categories",
    create: "/inventory/categories/create",
    edit: "/inventory/categories/edit/:id",
    show: "/inventory/categories/show/:id",
    meta: {
      label: "Categories",
      icon: <FolderOpen className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "inventory-suppliers",
    list: "/inventory/suppliers",
    create: "/inventory/suppliers/create",
    edit: "/inventory/suppliers/edit/:id",
    show: "/inventory/suppliers/show/:id",
    meta: {
      label: "Suppliers",
      icon: <Truck className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "inventory-movements",
    list: "/inventory/movements",
    create: "/inventory/movements/create",
    edit: "/inventory/movements/edit/:id",
    show: "/inventory/movements/show/:id",
    meta: {
      label: "Stock Movements",
      icon: <ArrowUpDown className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "purchase-orders",
    list: "/inventory/purchase-orders",
    create: "/inventory/purchase-orders/create",
    edit: "/inventory/purchase-orders/edit/:id",
    show: "/inventory/purchase-orders/show/:id",
    meta: {
      label: "Purchase Orders",
      icon: <FileCheck className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "goods-receipts",
    list: "/inventory/goods-receipts",
    create: "/inventory/goods-receipts/create",
    edit: "/inventory/goods-receipts/edit/:id",
    show: "/inventory/goods-receipts/show/:id",
    meta: {
      label: "Goods Received Notes",
      icon: <Package className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "purchase-invoices",
    list: "/inventory/purchase-invoices",
    create: "/inventory/purchase-invoices/create",
    edit: "/inventory/purchase-invoices/edit/:id",
    show: "/inventory/purchase-invoices/show/:id",
    meta: {
      label: "Purchase Invoices",
      icon: <Receipt className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "supplier-payments",
    list: "/inventory/supplier-payments",
    create: "/inventory/supplier-payments/create",
    show: "/inventory/supplier-payments/show/:id",
    meta: {
      label: "Payments Made",
      icon: <CreditCard className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "debit-notes",
    list: "/inventory/debit-notes",
    create: "/inventory/debit-notes/create",
    show: "/inventory/debit-notes/show/:id",
    meta: {
      label: "Debit Notes",
      icon: <FileBarChart className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },
  {
    name: "purchase-documents",
    list: "/inventory/purchase-documents",
    create: "/inventory/purchase-documents/create",
    edit: "/inventory/purchase-documents/edit/:id",
    meta: {
      label: "Vendor Invoice Uploads",
      icon: <FolderOpen className="h-4 w-4" />,
      parent: "inventory",
      canDelete: true,
    },
  },

  // 7. Device Integration
  {
    name: "devices",
    meta: {
      label: "Devices",
      icon: <Cpu className="h-4 w-4" />,
    },
  },
  {
    name: "devices/connectors",
    list: "/devices/connectors",
    create: "/devices/connectors/create",
    edit: "/devices/connectors/edit/:id",
    show: "/devices/connectors/show/:id",
    meta: {
      label: "Connector Devices",
      icon: <Cable className="h-4 w-4" />,
      parent: "devices",
      canDelete: true,
    },
  },
  {
    name: "devices/analyzers",
    list: "/devices/analyzers",
    create: "/devices/analyzers/create",
    edit: "/devices/analyzers/edit/:id",
    show: "/devices/analyzers/show/:id",
    meta: {
      label: "Analyzer Devices",
      icon: <Microscope className="h-4 w-4" />,
      parent: "devices",
      canDelete: true,
    },
  },
  {
    name: "devices/imaging",
    list: "/devices/imaging",
    create: "/devices/imaging/create",
    edit: "/devices/imaging/edit/:id",
    show: "/devices/imaging/show/:id",
    meta: {
      label: "Imaging Devices (PACS)",
      icon: <ScanLine className="h-4 w-4" />,
      parent: "devices",
      canDelete: true,
    },
  },
  {
    name: "devices/monitoring",
    list: "/devices/monitoring",
    meta: {
      label: "Monitoring Dashboard",
      icon: <MonitorDot className="h-4 w-4" />,
      parent: "devices",
    },
  },

  // 8. Billing
  {
    name: "billing",
    meta: {
      label: "Billing",
      icon: <DollarSign className="h-4 w-4" />,
    },
  },
  {
    name: "billing-entities",
    list: "/billing/entities",
    create: "/billing/entities/create",
    edit: "/billing/entities/edit/:id",
    show: "/billing/entities/show/:id",
    meta: {
      label: "Billing Entities",
      icon: <User className="h-4 w-4" />,
      parent: "billing",
      canDelete: true,
    },
  },
  {
    name: "invoices",
    list: "/billing/invoices",
    create: "/billing/invoices/create",
    edit: "/billing/invoices/edit/:id",
    show: "/billing/invoices/show/:id",
    meta: {
      label: "Invoices",
      icon: <Receipt className="h-4 w-4" />,
      parent: "billing",
      canDelete: true,
    },
  },
  {
    name: "payments",
    list: "/billing/payments",
    create: "/billing/payments/create",
    edit: "/billing/payments/edit/:id",
    show: "/billing/payments/show/:id",
    meta: {
      label: "Payments",
      icon: <CreditCard className="h-4 w-4" />,
      parent: "billing",
      canDelete: true,
    },
  },
  {
    name: "tenant-tax-types",
    list: "/billing/tax-types",
    create: "/billing/tax-types/create",
    edit: "/billing/tax-types/edit/:id",
    show: "/billing/tax-types/show/:id",
    meta: {
      label: "Tax Management",
      icon: <Percent className="h-4 w-4" />,
      parent: "billing",
      canDelete: true,
    },
  },

  // 'User Management' parent + Users + Roles resources retired
  // 2026-05-19. Identity admin lives at admin.onprosoft.com. The
  // /users and /users/roles routes are kept (and redirect at the
  // App.tsx level) so old bookmarks still resolve.
  // User Sessions moved under Core Settings — product-local concern.

  // 10. Core Settings
  {
    name: "core",
    meta: {
      label: "Core Settings",
      icon: <Settings className="h-4 w-4" />,
    },
  },
  {
    name: "tenant-user-sessions",
    list: "/users/sessions",
    show: "/users/sessions/show/:id",
    meta: {
      label: "Active Sessions",
      icon: <Clock className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "companies",
    list: "/core/companies",
    create: "/core/companies/create",
    edit: "/core/companies/edit/:id",
    show: "/core/companies/show/:id",
    meta: {
      label: "Company Profile",
      icon: <Building2 className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "branches",
    list: "/core/branches",
    create: "/core/branches/create",
    edit: "/core/branches/edit/:id",
    show: "/core/branches/show/:id",
    meta: {
      label: "Branch Management",
      icon: <MapPin className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "addresses",
    list: "/core/addresses",
    create: "/core/addresses/create",
    edit: "/core/addresses/edit/:id",
    show: "/core/addresses/show/:id",
    meta: {
      label: "Address Management",
      icon: <Globe className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "organization-settings",
    list: "/core/settings",
    edit: "/core/settings/edit",
    meta: {
      label: "Organization Settings",
      icon: <SlidersHorizontal className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "currency-management",
    list: "/core/currencies",
    meta: {
      label: "Currency Management",
      icon: <DollarSign className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "brand-management",
    list: "/core/branding",
    meta: {
      label: "Brand Management",
      icon: <Palette className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "appearance",
    list: "/core/appearance",
    meta: {
      label: "Appearance",
      icon: <LayoutGrid className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "document-sequences",
    list: "/core/document-sequences",
    meta: {
      label: "Prefix / Suffix",
      icon: <Hash className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    // Route unchanged (/document-studio) — this entry only moves it into the
    // Core Settings group, where the rest of the document configuration lives.
    name: "document-studio",
    list: "/document-studio",
    meta: {
      label: "Document Studio",
      icon: <FileCheck className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "holiday-calendar",
    list: "/core/holidays",
    meta: {
      label: "Holiday Calendar",
      icon: <CalendarDays className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "container-types",
    list: "/core/container-types",
    meta: {
      label: "Container Types",
      icon: <TestTube2 className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "settings-audit-trail",
    list: "/core/audit-trail",
    meta: {
      label: "Settings Audit Trail",
      icon: <ClipboardList className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "danger-zone",
    list: "/core/danger-zone",
    meta: {
      label: "Danger Zone",
      icon: <AlertTriangle className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "notification-settings",
    list: "/core/notifications",
    edit: "/core/notifications/edit",
    meta: {
      label: "Notification Settings",
      icon: <Bell className="h-4 w-4" />,
      parent: "core",
    },
  },
  {
    name: "notification-templates",
    list: "/core/notification-templates",
    create: "/core/notification-templates/create",
    edit: "/core/notification-templates/edit/:id",
    show: "/core/notification-templates/show/:id",
    meta: {
      label: "Notification Templates",
      icon: <Mail className="h-4 w-4" />,
      parent: "core",
      canDelete: true,
    },
  },

  // 11. Support
  {
    name: "support",
    meta: {
      label: "Support",
      icon: <LifeBuoy className="h-4 w-4" />,
    },
  },
  {
    name: "tenant-support-tickets",
    list: "/support/tickets",
    create: "/support/tickets/create",
    edit: "/support/tickets/edit/:id",
    show: "/support/tickets/show/:id",
    meta: {
      label: "Support Tickets",
      icon: <MessageSquare className="h-4 w-4" />,
      parent: "support",
      canDelete: true,
    },
  },
  {
    name: "knowledge-base",
    list: "/support/kb",
    meta: {
      label: "Knowledge Base",
      icon: <BookOpen className="h-4 w-4" />,
      parent: "support",
    },
  },

  // LabBOXX-InTutor — the in-app manual; visible to every user. Also feeds
  // the manual's live navigation overview.
  {
    name: "help",
    list: "/help",
    meta: {
      label: "LabBOXX-InTutor",
      icon: <GraduationCap className="h-4 w-4" />,
    },
  },
];

// Default export
export const resources = tenantResources;
